import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { bankingService } from '../../../services/bankingService';

const TransactionFilters = ({ 
  filters, 
  onFilterChange, 
  onClearFilters, 
  bankAccounts, 
  selectedAccount, 
  onAccountChange 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([
    { value: 'all', label: 'All Categories' }
  ]);
  const [loading, setLoading] = useState(false);

  const accountOptions = [
    { value: 'all', label: 'All Accounts' },
    ...(bankAccounts || [])?.map(account => ({
      value: account?.id,
      label: `${account?.bankName} (...${account?.accountNumber?.slice(-4)})`
    }))
  ];

  const typeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'income', label: 'Income' },
    { value: 'expense', label: 'Expense' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'reconciled', label: 'Reconciled' },
    { value: 'unreconciled', label: 'Unreconciled' },
    { value: 'pending', label: 'Pending' }
  ];

  // Load enhanced category options from service
  useEffect(() => {
    loadCategoryOptions();
  }, []);

  const loadCategoryOptions = async () => {
    try {
      setLoading(true);
      const enhancedOptions = await bankingService?.getEnhancedCategoryOptions();
      
      // Add "All Categories" option at the beginning
      const options = [
        { value: 'all', label: 'All Categories' },
        ...enhancedOptions
      ];
      
      setCategoryOptions(options);
    } catch (error) {
      console.error('Error loading category options:', error);
      // Keep fallback categories if there's an error
      setCategoryOptions([
        { value: 'all', label: 'All Categories' },
        { value: 'Revenue', label: 'Revenue' },
        { value: 'Office Supplies', label: 'Office Supplies' },
        { value: 'Marketing & Advertising', label: 'Marketing & Advertising' },
        { value: 'Travel & Entertainment', label: 'Travel & Entertainment' },
        { value: 'Utilities', label: 'Utilities' },
        { value: 'Professional Services', label: 'Professional Services' },
        { value: 'Equipment & Software', label: 'Equipment & Software' },
        { value: 'Rent & Facilities', label: 'Rent & Facilities' },
        { value: 'Bank Fees', label: 'Bank Fees' },
        { value: 'Other', label: 'Other' }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const activeFilterCount = Object.values(filters || {})?.filter(value => 
    value && value !== '' && value !== 'all'
  )?.length;

  const handleDateFromChange = (value) => {
    onFilterChange('dateFrom', value);
  };

  const handleDateToChange = (value) => {
    onFilterChange('dateTo', value);
  };

  const handleAmountFromChange = (value) => {
    onFilterChange('amountFrom', value);
  };

  const handleAmountToChange = (value) => {
    onFilterChange('amountTo', value);
  };

  // Enhanced Select component that handles header options
  const EnhancedSelect = ({ options, value, onChange, placeholder }) => {
    return (
      <select 
        className="w-full p-3 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
        value={value || 'all'}
        onChange={(e) => onChange(e?.target?.value)}
      >
        <option value="" disabled>{placeholder}</option>
        {options?.map(option => {
          if (option?.isHeader) {
            return (
              <option 
                key={option?.value} 
                value={option?.value} 
                disabled 
                style={{ 
                  fontWeight: 'bold', 
                  backgroundColor: '#f3f4f6', 
                  color: '#6b7280'
                }}
              >
                {option?.label}
              </option>
            );
          }
          return (
            <option key={option?.value} value={option?.value}>
              {option?.label}
            </option>
          );
        })}
      </select>
    );
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-4">
          <h3 className="text-lg font-semibold text-foreground">Filter Transactions</h3>
          {activeFilterCount > 0 && (
            <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">
              {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''} active
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {activeFilterCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearFilters}
              iconName="X"
              iconPosition="left"
            >
              Clear All
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            iconPosition="right"
          >
            {isExpanded ? 'Less Filters' : 'More Filters'}
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Account</label>
          <Select
            options={accountOptions}
            value={selectedAccount || 'all'}
            onChange={onAccountChange}
            placeholder="Select account"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Search</label>
          <Input
            type="text"
            placeholder="Search transactions..."
            value={filters?.search || ''}
            onChange={(e) => onFilterChange('search', e?.target?.value)}
            iconName="Search"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Type</label>
          <Select
            options={typeOptions}
            value={filters?.type || 'all'}
            onChange={(value) => onFilterChange('type', value)}
            placeholder="Select type"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Status</label>
          <Select
            options={statusOptions}
            value={filters?.status || 'all'}
            onChange={(value) => onFilterChange('status', value)}
            placeholder="Select status"
          />
        </div>
      </div>
      {isExpanded && (
        <div className="border-t border-border pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Category {loading && <span className="text-xs text-muted-foreground">(Loading...)</span>}
              </label>
              <EnhancedSelect
                options={categoryOptions}
                value={filters?.category || 'all'}
                onChange={(value) => onFilterChange('category', value)}
                placeholder="Select category"
              />
              {/* Category legend for account types */}
              {!loading && categoryOptions?.some(opt => opt?.isHeader) && (
                <div className="mt-2 text-xs text-muted-foreground">
                  <div className="flex items-center space-x-4">
                    <span className="flex items-center">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-1"></span>
                      Assets
                    </span>
                    <span className="flex items-center">
                      <span className="w-2 h-2 bg-red-500 rounded-full mr-1"></span>
                      Liabilities
                    </span>
                    <span className="flex items-center">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mr-1"></span>
                      Equity
                    </span>
                    <span className="flex items-center">
                      <span className="w-2 h-2 bg-emerald-500 rounded-full mr-1"></span>
                      Revenue
                    </span>
                    <span className="flex items-center">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mr-1"></span>
                      Expense
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Date From</label>
              <Input
                type="date"
                value={filters?.dateFrom || ''}
                onChange={(e) => handleDateFromChange(e?.target?.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Date To</label>
              <Input
                type="date"
                value={filters?.dateTo || ''}
                onChange={(e) => handleDateToChange(e?.target?.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Amount From</label>
              <Input
                type="number"
                placeholder="Min amount"
                value={filters?.amountFrom || ''}
                onChange={(e) => handleAmountFromChange(e?.target?.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Amount To</label>
              <Input
                type="number"
                placeholder="Max amount"
                value={filters?.amountTo || ''}
                onChange={(e) => handleAmountToChange(e?.target?.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Quick Dates</label>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const today = new Date();
                    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
                    onFilterChange('dateFrom', startOfMonth?.toISOString()?.split('T')?.[0]);
                    onFilterChange('dateTo', today?.toISOString()?.split('T')?.[0]);
                  }}
                >
                  This Month
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const today = new Date();
                    const thirtyDaysAgo = new Date(today);
                    thirtyDaysAgo?.setDate(thirtyDaysAgo?.getDate() - 30);
                    onFilterChange('dateFrom', thirtyDaysAgo?.toISOString()?.split('T')?.[0]);
                    onFilterChange('dateTo', today?.toISOString()?.split('T')?.[0]);
                  }}
                >
                  Last 30 Days
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-border">
          {filters?.search && (
            <div className="flex items-center space-x-1 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm">
              <span>Search: "{filters?.search}"</span>
              <button
                onClick={() => onFilterChange('search', '')}
                className="hover:bg-primary/20 rounded-full p-1"
              >
                <Icon name="X" size={12} />
              </button>
            </div>
          )}
          {filters?.type && filters?.type !== 'all' && (
            <div className="flex items-center space-x-1 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm">
              <span>Type: {filters?.type}</span>
              <button
                onClick={() => onFilterChange('type', 'all')}
                className="hover:bg-primary/20 rounded-full p-1"
              >
                <Icon name="X" size={12} />
              </button>
            </div>
          )}
          {filters?.status && filters?.status !== 'all' && (
            <div className="flex items-center space-x-1 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm">
              <span>Status: {filters?.status}</span>
              <button
                onClick={() => onFilterChange('status', 'all')}
                className="hover:bg-primary/20 rounded-full p-1"
              >
                <Icon name="X" size={12} />
              </button>
            </div>
          )}
          {filters?.category && filters?.category !== 'all' && (
            <div className="flex items-center space-x-1 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm">
              <span>Category: {categoryOptions?.find(opt => opt?.value === filters?.category)?.label || filters?.category}</span>
              <button
                onClick={() => onFilterChange('category', 'all')}
                className="hover:bg-primary/20 rounded-full p-1"
              >
                <Icon name="X" size={12} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TransactionFilters;